import { useCallback, useRef, useState } from 'react';
import clsx from 'clsx';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/Button';
import { useCreateCandidateImportRequest } from '../api/useCreateCandidateImportRequest';
import { showErrorToast } from '../../job_change_processes/lib/toast';

export function CandidateImportRequestsUploadTab() {
  const navigate = useNavigate();
  const [isDragging, setIsDragging] = useState(false);
  const dropAreaRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const createMutation = useCreateCandidateImportRequest();

  // ファイルバリデーション
  const isValidFile = useCallback((file: File): boolean => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      showErrorToast('CSVファイルのみアップロード可能です。');
      return false;
    }
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      showErrorToast('ファイルサイズは5MBまでです。');
      return false;
    }
    return true;
  }, []);

  const handleUpload = useCallback(
    async (file: File) => {
      if (!isValidFile(file)) return;

      try {
        const result = await createMutation.mutateAsync({
          file,
        });

        // 確認画面に遷移
        navigate({
          to: '/agent/candidates/candidate_import_requests/new/$encryptId',
          params: { encryptId: result.encrypt_id },
        });
      } catch (error) {
        showErrorToast(error instanceof Error ? error.message : 'アップロードに失敗しました。');
      }
    },
    [isValidFile, createMutation, navigate]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleUpload(files[0]);
      }
      e.target.value = '';
    },
    [handleUpload]
  );

  const handleButtonClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === dropAreaRef.current) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        handleUpload(files[0]);
      }
    },
    [handleUpload]
  );

  const isUploading = createMutation.isPending;

  return (
    <div className="max-w-[812px] space-y-lg">
      {/* ヘッダー */}
      <h2 className="text-heading-md">CSVアップロード</h2>

      {/* アップロードエリア */}
      <div className="flex flex-col gap-2">
        <div
          ref={dropAreaRef}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={clsx(
            'flex h-[224px] w-full flex-col items-center justify-center gap-3 rounded border-2 border-dashed p-6 transition-colors',
            isUploading
              ? 'pointer-events-none border-border bg-gray opacity-50'
              : isDragging
                ? 'border-primary bg-primary/10'
                : 'border-border bg-gray'
          )}
        >
          <img src="/agent/candidates/icon-upload.svg" alt="" width={48} height={48} />

          <div className="flex flex-col items-center gap-1">
            <p className="text-lg leading-[1.7] text-main">
              {isUploading ? 'アップロード中...' : 'ここにドラッグアンドドロップ'}
            </p>
            {!isUploading && <p className="text-base leading-[1.7] text-main">または</p>}
          </div>

          {!isUploading && (
            <div onClick={(e) => e.stopPropagation()}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={handleFileInputChange}
              />
              <Button color="secondary" size="md" type="button" onClick={handleButtonClick}>
                <img
                  src="/agent/candidates/icon-folder.svg"
                  alt=""
                  width={20}
                  height={20}
                  className="mr-1"
                />
                ファイルを選択
              </Button>
            </div>
          )}
        </div>

        <p className="text-body-xs text-sub">
          ※アップロードできるファイル数は1度に1ファイル、ファイルサイズは5MBまでです。
        </p>
      </div>
    </div>
  );
}
